// Shared by the entry and the modules it re-exports; imports only types from them.
import type { InitiativeRegistry } from './runtime'

const flag = import.meta.env.VITE_DESIGN_QA_SCENARIOS

// Production builds (release and PR previews alike) opt in explicitly; the release env leaves it unset so the
// guarded dynamic imports are dead code and the scenario chunks are never emitted.
export const DESIGN_QA_SCENARIOS_ENABLED: boolean = import.meta.env.DEV ? flag !== 'false' : flag === 'true'

export type DesignQaRole = 'implementation' | 'prototype'

/**
 * `VITE_DESIGN_QA_ROLE=prototype` on prototype branches: from `dedicated.env` in previews, from the branch name in dev
 * (`./vite`). Everything else is the implementation.
 */
export const designQaRole = (): DesignQaRole =>
  import.meta.env.VITE_DESIGN_QA_ROLE === 'prototype' ? 'prototype' : 'implementation'

export const OFF = 'off'

export const SCENARIO_PARAM = 'scenario'
export const SCENARIO_KEY = 'designQaScenario'

export function safely<T>(fn: () => T, fallback: T): T {
  try {
    return fn()
  } catch {
    return fallback
  }
}

/** Whether a scenario may be active this page load, read before loading any scenario code. */
export function mayHaveDesignQaScenario(search: string): boolean {
  if (new URLSearchParams(search).has(SCENARIO_PARAM)) return true
  return safely(() => window.sessionStorage.getItem(SCENARIO_KEY) !== null, false)
}

/** A `?<param>=` value; links pasted from prose often carry the sentence's closing punctuation, so it is dropped. */
export function readParam(search: string, param: string): string | undefined {
  return new URLSearchParams(search)
    .get(param)
    ?.trim()
    .replace(/[.,:;!?)\]'"`]+$/, '')
}

/** `href` with `?<param>=` set to `value`, or removed when `value` is null. */
export function withParam(href: string, param: string, value: string | null): string {
  const url = new URL(href)
  if (value === null) url.searchParams.delete(param)
  else url.searchParams.set(param, value)
  return url.toString()
}

/** Meridian's breakpoints, one viewport preset each. */
export const VIEWPORT_NAMES = ['xs', 'sm', 'md', 'lg', 'xl'] as const

export const VARIANT_PARAM = 'variant'
export const OVERLAYS_PARAM = 'overlays'

const VARIANT_KEY = 'designQaVariant'
const OVERLAYS_KEY = 'designQaOverlays'

/** A `?<param>=` value kept for the session, so it survives in-app navigation; `clearValue` clears it. */
function sessionParam(param: string, key: string, clearValue: string): string | undefined {
  return safely(() => {
    const value = readParam(window.location.search, param)
    if (value === clearValue) window.sessionStorage.removeItem(key)
    else if (value) window.sessionStorage.setItem(key, value)
    return window.sessionStorage.getItem(key) ?? undefined
  }, undefined)
}

const initialState = () => ({
  variant: DESIGN_QA_SCENARIOS_ENABLED ? sessionParam(VARIANT_PARAM, VARIANT_KEY, OFF) : undefined,
  overlaysHidden: DESIGN_QA_SCENARIOS_ENABLED && sessionParam(OVERLAYS_PARAM, OVERLAYS_KEY, 'on') === OFF,
  loadInitiatives: undefined as (() => Promise<InitiativeRegistry>) | undefined,
  scenarioFlags: undefined as Record<string, boolean | string> | undefined,
  scenarioStatus: null as { value: string; known: boolean } | null
})

/** What this page load has resolved: one object, so tests reset it instead of re-importing Meridian and MUI. */
export const designQaState = initialState()

/** The design variant a prototype renders (`?variant=<id>`, `off` clears it); always undefined when scenarios are off. */
export function getDesignQaVariant(): string | undefined {
  return designQaState.variant
}

/** `?overlays=off` hides the dock and the known-gap marks for manual screenshots; `?overlays=on` brings them back. */
export function areDesignQaOverlaysHidden(): boolean {
  return designQaState.overlaysHidden
}

export function setDesignQaScenarioFlags(flags: Record<string, boolean | string> | undefined): void {
  designQaState.scenarioFlags = flags && Object.keys(flags).length ? flags : undefined
}

/** Test only: a fresh page load's state. Nothing the apps import references it, so release bundles drop it. */
export function __resetDesignQaState(): void {
  Object.assign(designQaState, initialState())
}
